import axios from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

// Attach Bearer token to all requests at module load time
axios.interceptors.request.use((config) => {
  const token = Cookies.get('accessToken');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Function to check if token is expired
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime;
  } catch (error) {
    return true;
  }
};

// Function to get valid token
export const getValidToken = () => {
  const token = Cookies.get('accessToken');
  if (!token || isTokenExpired(token)) {
    return null;
  }
  return token;
};

// Function to clear authentication data
export const clearAuthData = () => {
  Cookies.remove('accessToken');
  localStorage.removeItem('email');
  localStorage.removeItem('role');
  localStorage.removeItem('id');
  localStorage.removeItem('storeName');
  localStorage.removeItem('storeCode');
  localStorage.removeItem('tableauToken');
  localStorage.removeItem('access');
  localStorage.removeItem('refresh_token');
};

// Function to store authentication data (supports SSO and legacy Google/Microsoft tokens)
export const storeAuthData = (authData) => {
  if (authData.accessToken) {
    Cookies.set('accessToken', authData.accessToken, { expires: 7 });
    // Extract access from self-issued JWTs (Google/Microsoft fallback path)
    if (!authData.access) {
      try {
        const decoded = jwtDecode(authData.accessToken);
        if (decoded.access) {
          const a = decoded.access;
          localStorage.setItem('access', Array.isArray(a) ? a.join(',') : a);
        }
      } catch {}
    }
  }
  if (authData.access) {
    const a = authData.access;
    localStorage.setItem('access', Array.isArray(a) ? a.join(',') : a);
  }
  if (authData.refresh_token) localStorage.setItem('refresh_token', authData.refresh_token);
  if (authData.email) localStorage.setItem('email', authData.email);
  if (authData.role) localStorage.setItem('role', authData.role);
  if (authData.id) localStorage.setItem('id', authData.id);
  if (authData.storeName) localStorage.setItem('storeName', authData.storeName);
  if (authData.storeCode) localStorage.setItem('storeCode', authData.storeCode);
  if (authData.tableauToken) localStorage.setItem('tableauToken', authData.tableauToken);
};

// Function to handle successful authentication (for both regular and Google login)
export const handleSuccessfulAuth = async (authData, navigate, toast) => {
  try {
    // Store authentication data
    storeAuthData(authData);

    // Generate Tableau JWT token if possible
    try {
      const resJWTTableau = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/generatejwt`,
        { withCredentials: true, timeout: 5000 }
      );
      if (resJWTTableau.data) {
        localStorage.setItem('tableauToken', resJWTTableau.data);
      }
    } catch (err) {
      console.warn('Failed to generate Tableau token:', err);
      // Don't block login for Tableau token failure
    }

    // Show success message
    if (toast) {
      toast({
        title: 'Login Successful',
        description: 'Welcome back!',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    }

    // Navigate to homepage via full page reload to ensure App.js re-evaluates auth state
    window.location.href = '/homepage';

    return true;
  } catch (error) {
    console.error('Error in handleSuccessfulAuth:', error);
    if (toast) {
      toast({
        title: 'Warning',
        description: 'Login successful, but some features may be limited.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }

    // Still navigate even if Tableau token fails
    window.location.href = '/homepage';
    
    return false;
  }
};

// Function to handle authentication error
export const handleAuthError = (navigate, toast) => {
  clearAuthData();
  
  if (toast) {
    toast({
      title: "Authentication Error",
      description: "Your session has expired. Please log in again.",
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "top",
    });
  }
  
  if (navigate) {
    navigate('/');
  } else {
    window.location.href = '/';
  }
};

// Axios response interceptor to handle 401 errors globally
export const setupAxiosInterceptors = (navigate, toast) => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        handleAuthError(navigate, toast);
      }
      return Promise.reject(error);
    }
  );
};

let _refreshing = false;

// Silently refresh the SSO access token using the stored refresh token
export const refreshSSOToken = async () => {
  if (_refreshing) return false;
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return false;
  _refreshing = true;
  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_API_URL}/auth/sso/refresh`,
      { refresh_token: refreshToken },
      { withCredentials: true }
    );
    if (data?.token) {
      Cookies.set('accessToken', data.token, { expires: 1 / 24 });
      if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
      return true;
    }
    return false;
  } catch {
    return false;
  } finally {
    _refreshing = false;
  }
};

// Hook for checking authentication status
export const useAuthCheck = () => {
  const token = getValidToken();
  return {
    isAuthenticated: !!token,
    token,
    decodedToken: token ? jwtDecode(token) : null,
  };
};
